import { useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  MDSButton,
  MDSDropDown,
  MDSFileUploader,
  MDSPanelActions,
  MDSPanelBody,
  MDSPanelHeader,
  MDSSelect,
  MDSTextArea,
  MDSToastSnackbar,
} from '@marqvision/mds/core';
import { IcoUpload } from '@marqvision/mds/assets';
import { MDSTypography } from '@marqvision/mds-v2';
import { IFile } from '@marqvision/mds/core/FileUploader';
import { getFileNameFromUrl } from '@marqvision/shared/src/utils';
import { useAddRequestMutation, useUploadRequestFileMutation } from '~/apis/customerRequest';
import { PiracyCommonEnumsCrCRTypeEnum } from '~/swagger/data-contracts';
import { CancelDialog } from '~/components/CancelDialog';
import { placeholdersByRequestType, requestTypeLabel } from '../@constants';
import { FormItem } from './@components/FormItem';

const useStyles = makeStyles({
  body: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  selectType: {
    width: '260px',
  },
  filePreview: {
    display: 'none',
  },
  fileUploader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  action: {
    justifyContent: 'flex-end',
  },
});

const typeList = Object.values(PiracyCommonEnumsCrCRTypeEnum).map((value) => ({ label: requestTypeLabel[value], value }));

type Props = {
  onListMode: () => void;
};

export const AddRequest = (props: Props) => {
  const { onListMode } = props;
  const classes = useStyles();

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);
  const [requestDetails, setRequestDetails] = useState<string>('');
  const [requestType, setRequestType] = useState<PiracyCommonEnumsCrCRTypeEnum>();
  const [attachmentFiles, setAttachmentFiles] = useState<IFile[]>([]);

  const uploadFileMutation = useUploadRequestFileMutation();
  const addRequestMutation = useAddRequestMutation();

  const isDisabled = !requestDetails.trim() || !requestType;

  const handleBack = () => {
    if (requestDetails || requestType || attachmentFiles.length) {
      setIsCancelDialogOpen(true);
      return;
    }
    onListMode();
  };

  const handleDialogClose = () => {
    setIsCancelDialogOpen(false);
  };

  const handleTypeChange = (value: unknown) => {
    setRequestType(value as PiracyCommonEnumsCrCRTypeEnum);
  };

  const handleTextChange = (value: string) => {
    setRequestDetails(value);
  };

  const handleFileChange = (files: IFile[]) => {
    setAttachmentFiles(files);
  };

  const uploadFile = async (fileName: string) => {
    const { data } = await uploadFileMutation.mutateAsync({ fileName });
    return data.url;
  };

  const handleAdd = async () => {
    if (!requestType) return;

    const { status } = await addRequestMutation.mutateAsync({
      crType: requestType,
      details: requestDetails,
      attachments: attachmentFiles.map(({ url: s3Url, name }) => ({ s3Url, name: name || getFileNameFromUrl(s3Url) })),
    });

    if (status === 200) {
      MDSToastSnackbar({ type: 'succeed', title: 'Request submitted' });
      onListMode();
    }
  };

  return (
    <>
      <CancelDialog isOpen={isCancelDialogOpen} onLeave={onListMode} onStay={handleDialogClose} />
      <MDSPanelHeader title="Add request" onBack={handleBack} />
      <MDSPanelBody className={classes.body}>
        <FormItem label="Type">
          <MDSDropDown
            value={requestType}
            list={typeList}
            onChange={handleTypeChange}
            renderAnchorComponent={(value, handleOpen) => (
              <MDSSelect className={classes.selectType} size="medium" value={value} onClick={handleOpen} />
            )}
          />
        </FormItem>

        <FormItem label="Request details">
          <MDSTextArea
            minRows={3}
            placeholder={requestType && placeholdersByRequestType[requestType]}
            value={requestDetails}
            onChange={handleTextChange}
          />
        </FormItem>

        <MDSFileUploader
          variant="chipList"
          className={classes.filePreview}
          files={attachmentFiles}
          onUpload={uploadFile}
          onChange={handleFileChange}
          renderTitleComponent={({ onAdd }) => (
            <div className={classes.fileUploader}>
              <MDSButton variant="text" color="blue" size="medium" startIcon={<IcoUpload />} onClick={onAdd}>
                Attach file
              </MDSButton>
              <MDSTypography variant="T13" color="color/content/neutral/secondary/normal">
                (Optional)
              </MDSTypography>
            </div>
          )}
        />
      </MDSPanelBody>
      <MDSPanelActions className={classes.action}>
        <MDSButton variant="outline" color="bluegray" size="large" onClick={handleBack}>
          Cancel
        </MDSButton>
        <MDSButton variant="fill" color="blue" size="large" disabled={isDisabled} onClick={handleAdd}>
          Submit
        </MDSButton>
      </MDSPanelActions>
    </>
  );
};